import { t } from "@lingui/macro";
import { useCallNavigationShortcuts } from "@repo/ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback } from "react";
import { Button } from "./ui/button";

export function CallNavigationControls({
	previousCallId,
	nextCallId,
	onNavigateToCall,
	shortcutsEnabled = true,
}: {
	previousCallId?: string | null;
	nextCallId?: string | null;
	onNavigateToCall?: (callId: string) => void;
	shortcutsEnabled?: boolean;
}) {
	const navigateToPreviousCall = useCallback(() => {
		if (previousCallId) onNavigateToCall?.(previousCallId);
	}, [onNavigateToCall, previousCallId]);
	const navigateToNextCall = useCallback(() => {
		if (nextCallId) onNavigateToCall?.(nextCallId);
	}, [nextCallId, onNavigateToCall]);

	useCallNavigationShortcuts({
		enabled: shortcutsEnabled,
		onPrevious: previousCallId ? navigateToPreviousCall : undefined,
		onNext: nextCallId ? navigateToNextCall : undefined,
	});

	return (
		<div className="hidden items-center gap-2 sm:flex" dir="ltr">
			<Button
				variant="outline"
				size="sm"
				onClick={navigateToPreviousCall}
				disabled={!previousCallId}
				aria-label={t({ id: "previousCall", message: "Previous call" })}
				data-testid="call-nav-prev"
			>
				<ChevronLeft className="h-4 w-4" />
				<span className="mx-1">
					{t({ id: "previousCall", message: "Previous call" })}
				</span>
				<kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
					K
				</kbd>
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={navigateToNextCall}
				disabled={!nextCallId}
				aria-label={t({ id: "nextCall", message: "Next call" })}
				data-testid="call-nav-next"
			>
				<span className="mx-1">
					{t({ id: "nextCall", message: "Next call" })}
				</span>
				<kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
					J
				</kbd>
				<ChevronRight className="h-4 w-4" />
			</Button>
		</div>
	);
}
